import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Известяване при нов deploy на фронта.
 *
 * При build-а се качва `version.json` ({"version":"<commit/таймстамп>"}) с
 * Cache-Control: no-cache. Тук на интервал дърпаме този файл; ако версията се
 * е сменила спрямо заредената при отваряне → има нов deploy и вдигаме флага
 * `newVersionAvailable$`. UI-ят показва ненатрапчив тостер „Нова версия —
 * презареди"; НЕ презареждаме автоматично, за да не губим незаписани промени.
 *
 * На локален `ng serve` файлът липсва (404) → нищо не се случва.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {

	private currentVersion: string = null;
	private timer: any = null;
	private readonly url = 'version.json';
	private readonly pollMs = 60 * 1000; // на всяка минута — да не седим със стар bundle

	/** true когато е открит нов deploy — UI-ят показва тостер „презареди". */
	readonly newVersionAvailable$ = new BehaviorSubject<boolean>(false);

	constructor(private zone: NgZone) { }

	start(): void {
		// Първоначален baseline.
		this.fetchVersion().then(v => { if (v) { this.currentVersion = v; } });
		// Polling-ът върви ИЗВЪН Angular zone, за да не тригерим change detection
		// на всеки tick.
		this.zone.runOutsideAngular(() => {
			this.timer = setInterval(() => this.check(), this.pollMs);
		});
	}

	/**
	 * Извиква се от тостера — презарежда ръчно, когато потребителят е готов.
	 *
	 * `location.reload()` НЕ е достатъчно — браузърите и CDN-ите често
	 * връщат cached index.html въпреки `Cache-Control: no-cache,no-store`
	 * (особено CloudFront с default cache behavior). Затова добавяме
	 * cache-buster `_v=<timestamp>` query, който гарантира свеж GET за
	 * index.html → оттам новите hashed JS бъндъли. Чистим предишен `_v=`,
	 * за да не се натрупва при следващи deploy-и.
	 */
	reloadNow(): void {
		const path = window.location.pathname;
		const hash = window.location.hash;
		const cleanSearch = window.location.search
			.replace(/[?&]_v=\d+/g, '')
			.replace(/^&/, '?');
		const sep = cleanSearch && cleanSearch.length > 0 ? '&' : '?';
		const target = path + cleanSearch + sep + '_v=' + Date.now() + hash;
		window.location.replace(target);
	}

	private check(): void {
		this.fetchVersion().then(v => {
			if (!v) {
				return;
			}
			if (this.currentVersion && v !== this.currentVersion) {
				// Нов deploy → спираме polling-а и вдигаме флага (в Angular zone,
				// за да се прерисува UI-ят). Презареждането е по избор на user-а.
				if (this.timer) { clearInterval(this.timer); this.timer = null; }
				this.zone.run(() => this.newVersionAvailable$.next(true));
			} else if (!this.currentVersion) {
				this.currentVersion = v;
			}
		});
	}

	private fetchVersion(): Promise<string> {
		return fetch(this.url + '?_=' + Date.now(), { cache: 'no-store' })
			.then(r => (r.ok ? r.json() : null))
			.then(j => (j ? j.version : null))
			.catch(() => null);
	}
}
